import { useCallback, useEffect, useRef, useState } from 'react';

/* ---------------------------------------------------------------------------
 * Dictation (speech → text) via the Web Speech API.
 *
 * Supported on Chrome/Edge (desktop + Android) and iOS/macOS Safari 14.5+.
 * The recognised text is appended through `onText`; interim results are
 * exposed separately so the UI can show "listening…" feedback. Language
 * follows the device (`navigator.language`), which is what reps expect when
 * they dictate in Hebrew on a Hebrew phone.
 * ------------------------------------------------------------------------- */

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

const getSpeechCtor = (): SpeechRecognitionCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

/**
 * iOS only lets Safari itself use speech recognition. Chrome, Firefox, Edge
 * and in-app browsers on iPhone/iPad are WebKit shells where the constructor
 * exists but `start()` silently fails, so we treat them as unsupported and
 * point reps at the keyboard's mic key instead.
 */
const isIosNonSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return iOS && /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|FBAN|FBAV|Instagram|Line\//.test(ua);
};

export const dictationSupported = () => !!getSpeechCtor() && !isIosNonSafari();

/** Hint shown in place of the Dictate button when the API is unavailable. */
export const dictationHint = (): string | null => {
  if (dictationSupported()) return null;
  if (/iPhone|iPad|iPod/.test(typeof navigator === 'undefined' ? '' : navigator.userAgent)) {
    return 'Dictation: use the 🎤 key on the keyboard (or open in Safari for the Dictate button).';
  }
  return null;
};

/** If recognition ends this quickly with nothing heard, the service is unavailable. */
const DEAD_SESSION_MS = 1500;

export function useDictation(onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  // iOS Safari quirks: it re-sends the whole result list (resultIndex 0) on
  // every event, and can end the session with the last phrase still marked
  // interim. So we commit finals by index (no duplicates) and flush whatever
  // is still interim when recognition ends.
  const committedRef = useRef(0);
  const interimRef = useRef('');

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = getSpeechCtor();
    if (!Ctor) return;
    if (recRef.current) {
      recRef.current.abort();
      recRef.current = null;
    }
    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    committedRef.current = 0;
    interimRef.current = '';
    rec.onresult = (e) => {
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i]!;
        const t = r[0]?.transcript ?? '';
        if (r.isFinal) {
          if (i >= committedRef.current) {
            finalText += (finalText ? ' ' : '') + t;
            committedRef.current = i + 1;
          }
        } else {
          interimText += (interimText ? ' ' : '') + t;
        }
      }
      if (finalText.trim()) onTextRef.current(finalText.trim());
      interimRef.current = interimText;
      setInterim(interimText);
    };
    rec.onerror = (e) => {
      // "no-speech" and "aborted" are routine; surface the rest.
      if (e.error && e.error !== 'no-speech' && e.error !== 'aborted') {
        setError(
          e.error === 'not-allowed'
            ? 'Microphone access was blocked. Allow it in the browser settings.'
            : `Dictation error: ${e.error}`,
        );
      }
    };
    rec.onend = () => {
      // Flush text Safari never promoted to "final".
      const leftover = interimRef.current.trim();
      if (leftover) onTextRef.current(leftover);
      interimRef.current = '';
      setListening(false);
      setInterim('');
      recRef.current = null;
    };
    setError(null);
    const startedAt = Date.now();
    const prevOnEnd = rec.onend;
    rec.onend = () => {
      const dead =
        Date.now() - startedAt < DEAD_SESSION_MS && committedRef.current === 0 && !interimRef.current;
      prevOnEnd?.();
      if (dead) {
        setError('Dictation is not available in this browser. Use the 🎤 key on the keyboard.');
      }
    };
    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch {
      setError('Could not start dictation.');
    }
  }, []);

  useEffect(() => () => recRef.current?.abort(), []);

  return { supported: dictationSupported(), listening, interim, error, start, stop };
}

/* ---------------------------------------------------------------------------
 * Voice memo (audio recording) via MediaRecorder, saved as a lead attachment.
 * Chrome produces audio/webm, Safari audio/mp4 — we keep whatever the browser
 * gives us so playback works on the same platform family.
 * ------------------------------------------------------------------------- */

export const voiceMemoSupported = () =>
  typeof window !== 'undefined' &&
  typeof MediaRecorder !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia;

const pickMime = () => {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported?.(c)) return c;
  }
  return '';
};



export interface VoiceMemo {
  file: File;
  seconds: number;
  url: string;
}

export function useVoiceMemo() {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const resolveRef = useRef<((m: VoiceMemo | null) => void) | null>(null);

  const cleanupTimer = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const start = useCallback(async () => {
    if (!voiceMemoSupported()) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        cleanupTimer();
        setRecording(false);
        const secs = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
        const type = rec.mimeType || mime || 'audio/webm';
        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(chunksRef.current, { type });
        const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
        const file = new File([blob], `voice-memo-${stamp}.${ext}`, { type });
        const memo: VoiceMemo = { file, seconds: secs, url: URL.createObjectURL(blob) };
        resolveRef.current?.(blob.size > 0 ? memo : null);
        resolveRef.current = null;
        recRef.current = null;
      };
      // No timeslice on purpose: Safari writes fragmented MP4, and a blob glued
      // from timesliced chunks does not play back in Safari itself ("Error" in
      // the audio control). Recording in one piece yields a complete file.
      rec.start();
      recRef.current = rec;
      startedAtRef.current = Date.now();
      setSeconds(0);
      setRecording(true);
      timerRef.current = window.setInterval(
        () => setSeconds(Math.round((Date.now() - startedAtRef.current) / 1000)),
        500,
      );
    } catch (e) {
      const name = (e as { name?: string }).name;
      setError(
        name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow it in the browser settings.'
          : 'Could not start recording.',
      );
    }
  }, []);

  /** Stops recording and resolves with the finished memo (null if empty). */
  const stop = useCallback(
    () =>
      new Promise<VoiceMemo | null>((resolve) => {
        const rec = recRef.current;
        if (!rec || rec.state === 'inactive') {
          resolve(null);
          return;
        }
        resolveRef.current = resolve;
        rec.stop();
      }),
    [],
  );

  useEffect(
    () => () => {
      cleanupTimer();
      const rec = recRef.current;
      if (rec && rec.state !== 'inactive') {
        rec.stream.getTracks().forEach((t) => t.stop());
        rec.stop();
      }
    },
    [],
  );

  return { supported: voiceMemoSupported(), recording, seconds, error, start, stop };
}

/* ---------------------------------------------------------------------------
 * Contact Picker API (Android Chrome). Lets a rep pull name/phone/email from
 * the phone's address book instead of typing them.
 * ------------------------------------------------------------------------- */

export interface PickedContact {
  name?: string;
  phone?: string;
  email?: string;
}

interface ContactsManagerLike {
  select(
    props: string[],
    opts?: { multiple?: boolean },
  ): Promise<{ name?: string[]; tel?: string[]; email?: string[] }[]>;
}

const getContacts = (): ContactsManagerLike | null =>
  (typeof navigator !== 'undefined' &&
    (navigator as unknown as { contacts?: ContactsManagerLike }).contacts) ||
  null;

export const contactPickerSupported = () => !!getContacts();

export const pickContact = async (): Promise<PickedContact | null> => {
  const contacts = getContacts();
  if (!contacts) return null;
  try {
    const [c] = await contacts.select(['name', 'tel', 'email'], { multiple: false });
    if (!c) return null;
    return { name: c.name?.[0], phone: c.tel?.[0], email: c.email?.[0] };
  } catch {
    return null; // user cancelled or picker unavailable
  }
};
